import type {
    FeedsPreferences,
    PushPreferenceInput,
    PushPreferencesResponse as FeedsPushPreferencesResponse,
} from '@stream-io/feeds-client'
import type { PushPreference, PushPreferencesResponse as ChatPushPreferencesResponse } from 'stream-chat'

import { NotificationCategory, NotificationCategoryLevels, NotificationLevel } from '@/types'

// Should match the activity type used when adding posts through Manager App
const FEED_POST_ACTIVITY_TYPE = 'post'

export const CATEGORY_SUPPORTS_PUSH: Record<NotificationCategory, boolean> = {
    [NotificationCategory.chatMessages]: true,
    [NotificationCategory.feedPost]: true,
    [NotificationCategory.repliesToComments]: true,
    [NotificationCategory.mentions]: true,
}

const isPushEnabled = (levels: NotificationCategoryLevels, category: NotificationCategory): boolean => (
    levels[category] === NotificationLevel.pushAndInApp
)

export const buildChatPushPreference = (
    levels: NotificationCategoryLevels,
): Pick<PushPreference, 'chat_level' | 'chat_preferences'> => {
    const messagesOn = isPushEnabled(levels, NotificationCategory.chatMessages)
    const mentionsOn = isPushEnabled(levels, NotificationCategory.mentions)
    const chatLevel = messagesOn ? 'all' : (mentionsOn ? 'direct_mentions' : 'none')
    const mentionPreference = mentionsOn ? 'all' : 'none'

    return {
        chat_level: chatLevel,
        chat_preferences: {
            direct_mentions: mentionPreference,
            group_mentions: mentionPreference,
            channel_mentions: mentionPreference,
            here_mentions: mentionPreference,
            role_mentions: mentionPreference,
        },
    }
}

export const buildFeedsPushPreference = (
    levels: NotificationCategoryLevels,
): Pick<PushPreferenceInput, 'feeds_level' | 'feeds_preferences'> => {
    const mentionPreference = isPushEnabled(levels, NotificationCategory.mentions) ? 'all' : 'none'
    const feedPostPreference = isPushEnabled(levels, NotificationCategory.feedPost) ? 'all' : 'none'

    const feedsPreferences: FeedsPreferences = {
        comment_reply: isPushEnabled(levels, NotificationCategory.repliesToComments) ? 'all' : 'none',
        mention: mentionPreference,
        comment_mention: mentionPreference,
        custom_activity_types: { [FEED_POST_ACTIVITY_TYPE]: feedPostPreference },
    }

    const allOff = [
        feedsPreferences.comment_reply,
        feedsPreferences.mention,
        feedsPreferences.comment_mention,
        feedPostPreference,
    ].every(value => value === 'none')

    return {
        feeds_level: allOff ? 'none' : 'all',
        feeds_preferences: feedsPreferences,
    }
}

export const derivePushEnabledFromResponses = (
    chatResponse: ChatPushPreferencesResponse | undefined,
    feedsResponse: FeedsPushPreferencesResponse | undefined,
): Record<NotificationCategory, boolean> => {
    const chatMessagesOn = chatResponse?.chat_level === 'all'
    const chatMentionsOn = chatResponse?.chat_level === 'all'
        || chatResponse?.chat_level === 'direct_mentions'
        || chatResponse?.chat_level === 'mentions'
        || chatResponse?.chat_preferences?.direct_mentions === 'all'

    const feedsPreferences = feedsResponse?.feeds_preferences

    return {
        [NotificationCategory.chatMessages]: chatMessagesOn,
        [NotificationCategory.feedPost]: feedsPreferences?.custom_activity_types?.[FEED_POST_ACTIVITY_TYPE] === 'all',
        [NotificationCategory.repliesToComments]: feedsPreferences?.comment_reply === 'all',
        [NotificationCategory.mentions]: chatMentionsOn || feedsPreferences?.mention === 'all',
    }
}
